import { useCallback, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { TaskStatus } from '@weave/schema-gen-ts/dist/schemas/task-center/shared/v1/enums.pb';
import { CallIntelMutations, CallIntelTypes } from '@frontend/api-call-intel';
import { getUser } from '@frontend/auth-helpers';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { theme } from '@frontend/theme';
import {
  emptyStateGraphics,
  NakedUl,
  SkeletonLoaders,
  Text,
  TextLink,
  useAlert,
  useModalControl,
  styles as designSystemStyles,
} from '@frontend/design-system';
import { useCallIntelDefaultsShallowStore } from '../hooks';
import { CallFlagsData } from '../types';
import { callStatusUtils, generateTrackingId } from '../utils';
import { CallAnalyzeStatus } from './call-analyze-status';
import { CallIntelChip } from './chip';
import { TaskStatusSwitcher } from './input/task-status-switcher';
import { TaskModal } from './modal/task-modal';

// Type definitions

type BaseTaskProps = {
  isDemoAccount?: boolean;
  onHighlightTask?: (taskId: string) => void;
  tasks: CallIntelTypes.Task[];
};

export type CallTasksProps = BaseTaskProps & {
  call: CallIntelTypes.Call;
  callParticipantsMetadata: CallIntelTypes.CallParticipantsMetadata;
  isLoading?: boolean;
  onUpdateTasks?: (call: CallIntelTypes.Call, tasks: CallIntelTypes.Task[]) => void;
  onDeleteTask?: (call: CallIntelTypes.Call, taskId: string) => void;
};

type CallTaskProps = BaseTaskProps & {
  openTaskModal: (idx: number) => void;
  onStatusChange: (taskId: string, newStatus: TaskStatus) => void;
};

type TaskLoaderProps = { count: number };

const DEFAULT_TASK_IDX = -1;

const TaskLoader = ({ count }: TaskLoaderProps) => {
  const loadersToShow = Math.min(count, 3);
  return (
    <>
      {Array.from({ length: loadersToShow }).map((_, idx) => (
        <div
          key={idx}
          css={css`
            padding: ${theme.spacing(1.5, 2)};
          `}
        >
          <SkeletonLoaders.Loader height={theme.spacing(3.5)} />
          <div
            css={css`
              margin-top: ${theme.spacing(1)};
              display: flex;
              gap: ${theme.spacing(1)};
            `}
          >
            <SkeletonLoaders.Loader height={theme.spacing(3)} width={theme.spacing(12)} />
            <SkeletonLoaders.Loader height={theme.spacing(3)} width={theme.spacing(12)} />
          </div>
        </div>
      ))}
    </>
  );
};

const EmptyTask = ({ isFailedCall, isPoorAudio, isSkippedCall }: CallFlagsData) => {
  const { t } = useTranslation('analytics');
  const EmptyStateGraphicNoTasks = emptyStateGraphics.noCallFollowUps;
  return (
    <div css={styles.emptyStateWrapper}>
      <div
        css={css`
          width: 100%;
        `}
      >
        <CallAnalyzeStatus
          isFailedCall={isFailedCall}
          isPoorAudio={isPoorAudio}
          isSkippedCall={isSkippedCall}
          type='task'
        />
      </div>
      <EmptyStateGraphicNoTasks height={200} width={200} />
      <Text css={styles.emptyStateText}>{t('No Tasks for this call')}</Text>
    </div>
  );
};

const CallTask = ({ isDemoAccount, openTaskModal, onStatusChange, onHighlightTask, tasks }: CallTaskProps) => {
  const { chipVariants, dataLabels } = useCallIntelDefaultsShallowStore('chipVariants', 'dataLabels');

  return (
    <NakedUl css={styles.list}>
      {tasks.map((task, idx) => (
        <li
          data-trackingid={generateTrackingId({ customName: 'task-card-open' })}
          key={task.id}
          onClick={() => openTaskModal(idx)}
        >
          <div css={styles.taskTextWrapper}>
            <Text
              css={[
                styles.taskText,
                designSystemStyles.truncate,
                task.status === TaskStatus.STATUS_COMPLETED && styles.strike,
              ]}
            >
              {task.title}
            </Text>
            {task.citationTime && (
              <span css={styles.citationTime}>
                (
                <TextLink
                  data-testid='citation-time-link'
                  onClick={(e) => {
                    e.stopPropagation();
                    task.id && onHighlightTask?.(task.id);
                  }}
                  trackingId={generateTrackingId({
                    component: 'task-card',
                    context: 'timestamp-click',
                    isDemoAccount,
                  })}
                >
                  {task.citationTime}
                </TextLink>
                )
              </span>
            )}
            <Icon name='expand-small' css={styles.icon} />
          </div>
          <div css={styles.taskActionWrapper}>
            <TaskStatusSwitcher
              defaultOptionValue={task.status}
              onStatusChange={(newStatus) => {
                task.id && onStatusChange(task.id, newStatus);
              }}
              trackingId={generateTrackingId({ customName: 'task-card-status-switcher' })}
              value={task.status}
            />

            <CallIntelChip
              as='category'
              chipVariants={chipVariants}
              dataKey={task.type}
              label={dataLabels.taskTypes?.[task.type] || task.type}
              key={task.type}
            />
          </div>
        </li>
      ))}
    </NakedUl>
  );
};

export const CallTasks = ({
  call,
  callParticipantsMetadata,
  isDemoAccount,
  isLoading,
  tasks,
  onUpdateTasks,
  onDeleteTask,
  onHighlightTask,
}: CallTasksProps) => {
  const { t } = useTranslation('analytics');
  const alert = useAlert();
  const { modalProps, triggerProps, closeModal } = useModalControl();

  const [selectedTaskIdx, setSelectedTaskIdx] = useState<number>(DEFAULT_TASK_IDX);

  const user = getUser();

  const callFlags = callStatusUtils.getCallFlags(call);

  const selectedTask = useMemo(() => tasks[selectedTaskIdx], [tasks, selectedTaskIdx]);

  const handleOpenTaskModal = useCallback(
    (idx: number) => {
      setSelectedTaskIdx(idx);
      triggerProps.onClick();
    },
    [triggerProps]
  );

  const handlePreviousTask = useCallback(() => {
    setSelectedTaskIdx((prevIdx) => Math.max(prevIdx - 1, 0));
  }, []);

  const handleNextTask = useCallback(() => {
    setSelectedTaskIdx((prevIdx) => Math.min(prevIdx + 1, (tasks.length || 0) - 1));
  }, [tasks]);

  const handleModalClose = useCallback(() => {
    closeModal();
    setSelectedTaskIdx(DEFAULT_TASK_IDX);
  }, [closeModal]);

  const { mutate: updateTask } = CallIntelMutations.useUpdateTask({
    onSuccess: (_: any, taskPayload) => {
      if (taskPayload.status === undefined) return;

      const newStatus: TaskStatus = taskPayload.status;

      const updatedTasks: CallIntelTypes.Task[] = tasks.map((task) =>
        task.id === taskPayload.id ? { ...task, status: newStatus } : task
      );

      onUpdateTasks?.(call, updatedTasks);
    },
    onError: () => {
      alert.error(t('Unable to update task. Please try again.'));
    },
    isDemoAccount,
  });

  const { mutate: deleteTask } = CallIntelMutations.useDeleteTask({
    onSuccess: (_: any, { id: taskId }) => {
      onDeleteTask?.(call, taskId);
      alert.success(t('Successfully deleted task!'));
    },
    onError: () => {
      alert.error(t('Unable to delete task. Please try again.'));
    },
    isDemoAccount,
  });

  const handleStatusChange = useCallback(
    (taskId: string, newStatus: TaskStatus) => {
      if (taskId && user?.userID) {
        updateTask({ id: taskId, status: newStatus, updatedBy: user.userID });
      }
    },
    [updateTask, user?.userID]
  );

  const handleTaskDelete = useCallback(
    (taskId: string) => {
      deleteTask({ id: taskId });
      handleModalClose();
    },
    [deleteTask, handleModalClose]
  );

  if (isLoading) return <TaskLoader count={call.taskCount || 1} />;

  if (tasks?.length === 0) return <EmptyTask {...callFlags} />;

  return (
    <>
      <CallTask
        isDemoAccount={isDemoAccount}
        onHighlightTask={onHighlightTask}
        onStatusChange={handleStatusChange}
        openTaskModal={handleOpenTaskModal}
        tasks={tasks}
      />

      {tasks.length && !!selectedTask && (
        <TaskModal
          call={call}
          modalProps={modalProps}
          onClose={handleModalClose}
          onNext={handleNextTask}
          onPrevious={handlePreviousTask}
          onStatusChange={handleStatusChange}
          onDeleteTaskConfirm={handleTaskDelete}
          paginateParams={{ start: selectedTaskIdx + 1, total: tasks.length }}
          task={selectedTask}
          callParticipantsMetadata={callParticipantsMetadata}
        />
      )}
    </>
  );
};

const styles = {
  citationTime: css`
    margin-right: ${theme.spacing(1)};
  `,

  emptyStateText: css`
    color: ${theme.colors.neutral50};
  `,

  emptyStateWrapper: css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: ${theme.spacing(2, 2)};
  `,

  icon: css`
    cursor: pointer;
  `,

  list: css`
    li {
      padding: ${theme.spacing(1.5, 2)};
    }

    li:not(:last-child) {
      border-bottom: 1px solid ${theme.colors.neutral20};
    }

    li:hover {
      background-color: ${theme.colors.neutral5};
      cursor: pointer;
    }
  `,

  notificationBadge: css`
    height: ${theme.spacing(2)};
    width: ${theme.spacing(2)};
    padding: ${theme.spacing(0, 0.5)};
    border-radius: ${theme.borderRadius.full};
  `,

  strike: css`
    text-decoration: line-through;
  `,

  taskActionWrapper: css`
    display: flex;
    align-items: center;
    margin-top: ${theme.spacing(1)};
    gap: ${theme.spacing(1)};
  `,

  taskText: css`
    max-width: 332px;
  `,

  taskTextWrapper: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `,
};
